import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Users, Search, Plus, X, Star, Phone, Mail, ExternalLink, ChevronRight,
  ChevronDown, Copy, Check, Mic, MicOff, Send, ArrowRight, Trash2,
  Edit2, Eye, EyeOff, Link, MessageSquare, Clock, Loader2, Briefcase,
  MapPin, FileText, AlertTriangle, Save, Globe
} from 'lucide-react';
import { supabase } from '../supabase';
import ApplicantMessaging from '../components/ApplicantMessaging';

const getAdminEmail = () => {
  try { return JSON.parse(localStorage.getItem('workvan_internal_auth'))?.email; } catch(e) { return null; }
};

// --- Constants ---
const STAGES = ['new', 'screening', 'interview', 'offer', 'hired', 'rejected'];
const STAGE_ORDER = { new: 0, screening: 1, interview: 2, offer: 3, hired: 4, rejected: 5 };
const STAGE_COLORS = {
  new: '#3498db', screening: '#f39c12', interview: '#9b59b6',
  offer: '#4ecca3', hired: '#27ae60', rejected: '#e74c3c'
};
const ROLE_COLORS = { director: '#f1c40f', recruiter: '#9b59b6', affiliate: '#3498db' };
const NEXT_STAGE = { new: 'screening', screening: 'interview', interview: 'offer', offer: 'hired' };

const EMAIL_PRESETS = [
  { key: 'initial_outreach', label: 'Initial Outreach' },
  { key: 'schedule_interview', label: 'Schedule Interview' },
  { key: 'follow_up', label: 'Follow Up' },
  { key: 'offer', label: 'Offer' },
  { key: 'rejection', label: 'Rejection' },
  { key: 'custom', label: 'Custom' },
];

// --- Helpers ---
function relativeTime(dateStr) {
  if (!dateStr) return '';
  const now = new Date();
  const d = new Date(dateStr);
  const diffMs = now - d;
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days === 1) return 'yesterday';
  if (days < 30) return `${days}d ago`;
  const months = Math.floor(days / 30);
  return `${months}mo ago`;
}

function slugify(str) {
  return str.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
}

// --- Styles ---
const s = {
  page: { minHeight: '100vh', background: '#0a0a0a', color: '#e0e0e0', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' },
  header: { padding: '16px', borderBottom: '1px solid #2a2a2a', position: 'sticky', top: 0, background: '#0a0a0a', zIndex: 20 },
  title: { fontSize: '20px', fontWeight: 700, color: '#fff', margin: 0 },
  tabs: { display: 'flex', gap: '0', marginTop: '12px', borderRadius: '8px', overflow: 'hidden', border: '1px solid #2a2a2a' },
  tab: (active) => ({
    flex: 1, padding: '10px 16px', textAlign: 'center', fontSize: '14px', fontWeight: 600,
    background: active ? '#ff6b35' : '#1a1a1a', color: active ? '#fff' : '#999',
    border: 'none', cursor: 'pointer', transition: 'all 0.2s'
  }),
  pillBar: { display: 'flex', gap: '6px', overflowX: 'auto', padding: '12px 16px 0', WebkitOverflowScrolling: 'touch' },
  pill: (active) => ({
    padding: '6px 14px', borderRadius: '20px', fontSize: '13px', fontWeight: 500, whiteSpace: 'nowrap',
    background: active ? '#ff6b35' : '#1a1a1a', color: active ? '#fff' : '#aaa',
    border: `1px solid ${active ? '#ff6b35' : '#2a2a2a'}`, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px'
  }),
  badge: (color) => ({
    display: 'inline-block', padding: '2px 8px', borderRadius: '12px', fontSize: '11px', fontWeight: 600,
    background: color + '22', color: color, border: `1px solid ${color}44`
  }),
  searchWrap: { padding: '8px 16px' },
  searchInput: {
    width: '100%', padding: '10px 12px 10px 36px', borderRadius: '8px', border: '1px solid #2a2a2a',
    background: '#1a1a1a', color: '#e0e0e0', fontSize: '14px', outline: 'none', boxSizing: 'border-box'
  },
  card: {
    margin: '8px 16px', padding: '14px 16px', background: '#1a1a1a', borderRadius: '10px',
    border: '1px solid #2a2a2a', cursor: 'pointer', transition: 'border-color 0.2s'
  },
  cardRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  name: { fontSize: '16px', fontWeight: 600, color: '#fff' },
  subText: { fontSize: '13px', color: '#888', marginTop: '2px' },
  expandedSection: { marginTop: '12px', paddingTop: '12px', borderTop: '1px solid #2a2a2a' },
  infoRow: { display: 'flex', alignItems: 'center', gap: '8px', padding: '6px 0', fontSize: '14px', color: '#ccc' },
  actionBar: { display: 'flex', gap: '8px', overflowX: 'auto', padding: '12px 0', WebkitOverflowScrolling: 'touch' },
  actionBtn: (bg) => ({
    padding: '10px 16px', borderRadius: '8px', border: 'none', fontSize: '13px', fontWeight: 600,
    color: '#fff', background: bg, cursor: 'pointer', whiteSpace: 'nowrap', display: 'flex',
    alignItems: 'center', gap: '6px', minHeight: '44px', minWidth: '48px', flexShrink: 0
  }),
  activityItem: { padding: '8px 0', borderBottom: '1px solid #1a1a1a', fontSize: '13px', color: '#aaa' },
  modal: {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', zIndex: 100,
    display: 'flex', alignItems: 'flex-end', justifyContent: 'center'
  },
  modalContent: {
    background: '#1a1a1a', borderRadius: '16px 16px 0 0', width: '100%', maxWidth: '600px',
    maxHeight: '90vh', overflow: 'auto', padding: '20px', position: 'relative'
  },
  modalFull: {
    background: '#1a1a1a', borderRadius: '16px 16px 0 0', width: '100%', maxWidth: '600px',
    maxHeight: '92vh', overflow: 'auto', padding: '20px', position: 'relative'
  },
  input: {
    width: '100%', padding: '12px', borderRadius: '8px', border: '1px solid #2a2a2a',
    background: '#0a0a0a', color: '#e0e0e0', fontSize: '14px', outline: 'none', boxSizing: 'border-box'
  },
  textarea: {
    width: '100%', padding: '12px', borderRadius: '8px', border: '1px solid #2a2a2a',
    background: '#0a0a0a', color: '#e0e0e0', fontSize: '14px', outline: 'none',
    minHeight: '100px', resize: 'vertical', fontFamily: 'inherit', boxSizing: 'border-box'
  },
  label: { display: 'block', fontSize: '13px', fontWeight: 600, color: '#aaa', marginBottom: '4px', marginTop: '12px' },
  select: {
    width: '100%', padding: '12px', borderRadius: '8px', border: '1px solid #2a2a2a',
    background: '#0a0a0a', color: '#e0e0e0', fontSize: '14px', outline: 'none', boxSizing: 'border-box'
  },
  primaryBtn: {
    width: '100%', padding: '14px', borderRadius: '8px', border: 'none', fontSize: '15px',
    fontWeight: 600, color: '#fff', background: '#ff6b35', cursor: 'pointer', marginTop: '16px'
  },
  emptyState: { textAlign: 'center', padding: '60px 20px', color: '#666', fontSize: '15px' },
  stars: { display: 'flex', gap: '2px' },
};

// --- Star Rating Component ---
function StarRating({ rating, onRate, size = 16 }) {
  return (
    <div style={s.stars} onClick={(e) => e.stopPropagation()}>
      {[1, 2, 3, 4, 5].map(i => (
        <button
          key={i}
          onClick={(e) => { e.stopPropagation(); onRate(rating === i ? 0 : i); }}
          style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px', minWidth: '28px', minHeight: '28px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
        >
          <Star size={size} fill={i <= rating ? '#f1c40f' : 'none'} color={i <= rating ? '#f1c40f' : '#555'} />
        </button>
      ))}
    </div>
  );
}

// --- Voice Note Input ---
function VoiceNoteInput({ onSave, onCancel, saving }) {
  const [text, setText] = useState('');
  const [recording, setRecording] = useState(false);
  const [wasVoice, setWasVoice] = useState(false);
  const recognitionRef = useRef(null);

  const toggleRecording = () => {
    if (recording) {
      recognitionRef.current?.stop();
      setRecording(false);
      return;
    }
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) {
      alert('Speech recognition not supported in this browser.');
      return;
    }
    const recognition = new SpeechRecognition();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = 'en-US';
    let finalText = text;
    recognition.onresult = (event) => {
      let interim = '';
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const transcript = event.results[i][0].transcript;
        if (event.results[i].isFinal) {
          finalText += (finalText ? ' ' : '') + transcript;
        } else {
          interim += transcript;
        }
      }
      setText(finalText + (interim ? ' ' + interim : ''));
      setWasVoice(true);
    };
    recognition.onerror = () => setRecording(false);
    recognition.onend = () => setRecording(false);
    recognitionRef.current = recognition;
    recognition.start();
    setRecording(true);
  };

  return (
    <div style={{ marginTop: '12px' }}>
      <div style={{ position: 'relative' }}>
        <textarea
          style={s.textarea}
          placeholder="Type a note or tap the mic to dictate..."
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={3}
        />
        <button
          onClick={toggleRecording}
          style={{
            position: 'absolute', right: '8px', bottom: '8px', width: '40px', height: '40px',
            borderRadius: '50%', border: 'none', cursor: 'pointer', display: 'flex',
            alignItems: 'center', justifyContent: 'center',
            background: recording ? '#e74c3c' : '#2a2a2a',
            animation: recording ? 'pulse 1s ease-in-out infinite' : 'none'
          }}
        >
          {recording ? <MicOff size={18} color="#fff" /> : <Mic size={18} color="#ccc" />}
        </button>
      </div>
      <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
        <button onClick={onCancel} style={{ ...s.actionBtn('#2a2a2a'), flex: 1, justifyContent: 'center' }}>Cancel</button>
        <button
          onClick={() => text.trim() && onSave(text.trim(), wasVoice)}
          disabled={!text.trim() || saving}
          style={{ ...s.actionBtn('#ff6b35'), flex: 1, justifyContent: 'center', opacity: text.trim() && !saving ? 1 : 0.5 }}
        >
          {saving ? <Loader2 size={16} className="spin" /> : <Save size={16} />}
          Save Note
        </button>
      </div>
    </div>
  );
}

// --- Email Modal ---
function EmailModal({ applicant, onClose, onSent }) {
  const [preset, setPreset] = useState(null);
  const [customPrompt, setCustomPrompt] = useState('');
  const [loading, setLoading] = useState(false);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);
  const [generated, setGenerated] = useState(false);
  const [error, setError] = useState('');

  const generate = async (action) => {
    if (action === 'custom' && !customPrompt.trim()) {
      setPreset('custom');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const res = await fetch('https://workvanapp.com/api/recruitment-ai', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action,
          custom_instructions: action === 'custom' ? customPrompt : undefined,
          applicant: {
            name: applicant.name,
            email: applicant.email,
            phone: applicant.phone,
            role_interest: applicant.role_interest,
            city: applicant.city,
            state: applicant.state,
            experience_summary: applicant.experience_summary,
            stage: applicant.stage,
          }
        })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'AI generation failed');
      setSubject(data.subject || '');
      setBody(data.body || '');
      setGenerated(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const sendEmail = async () => {
    setSending(true);
    setError('');
    try {
      const res = await fetch('https://workvanapp.com/api/send-email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          to: applicant.email,
          subject,
          text: body,
          from: 'Work Van <noreply@workvanapp.com>'
        })
      });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || 'Failed to send email');
      }
      // Log activity
      await supabase.from('recruitment_activity').insert({
        applicant_id: applicant.id,
        type: 'email_sent',
        content: `Subject: ${subject}\n\n${body}`,
        created_by: getAdminEmail()
      });
      onSent();
    } catch (err) {
      setError(err.message);
    } finally {
      setSending(false);
    }
  };

  return (
    <div style={s.modal} onClick={onClose}>
      <div style={s.modalFull} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <h3 style={{ margin: 0, color: '#fff', fontSize: '17px' }}>Email {applicant.name}</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '8px' }}>
            <X size={20} color="#999" />
          </button>
        </div>

        {!generated && !loading && (
          <>
            <p style={{ fontSize: '13px', color: '#888', margin: '0 0 12px' }}>Choose a template or write custom instructions:</p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
              {EMAIL_PRESETS.map(p => (
                <button
                  key={p.key}
                  onClick={() => p.key === 'custom' ? setPreset('custom') : generate(p.key)}
                  style={{
                    padding: '10px 16px', borderRadius: '8px', border: '1px solid #2a2a2a',
                    background: '#0a0a0a', color: '#e0e0e0', fontSize: '13px', fontWeight: 500,
                    cursor: 'pointer', minHeight: '44px'
                  }}
                >
                  {p.label}
                </button>
              ))}
            </div>
            {preset === 'custom' && (
              <div style={{ marginTop: '12px' }}>
                <textarea
                  style={s.textarea}
                  placeholder="Describe what the email should say..."
                  value={customPrompt}
                  onChange={(e) => setCustomPrompt(e.target.value)}
                  rows={3}
                />
                <button
                  onClick={() => generate('custom')}
                  disabled={!customPrompt.trim()}
                  style={{ ...s.primaryBtn, opacity: customPrompt.trim() ? 1 : 0.5 }}
                >
                  Generate Email
                </button>
              </div>
            )}
          </>
        )}

        {loading && (
          <div style={{ textAlign: 'center', padding: '40px 0', color: '#888' }}>
            <Loader2 size={28} color="#ff6b35" style={{ animation: 'spin 1s linear infinite' }} />
            <p style={{ marginTop: '12px' }}>Generating email...</p>
          </div>
        )}

        {generated && (
          <>
            <label style={s.label}>Subject</label>
            <input
              style={s.input}
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
            />
            <label style={{ ...s.label, marginTop: '12px' }}>Body</label>
            <textarea
              style={{ ...s.textarea, minHeight: '200px' }}
              value={body}
              onChange={(e) => setBody(e.target.value)}
            />
            {error && <p style={{ color: '#e74c3c', fontSize: '13px', marginTop: '8px' }}>{error}</p>}
            <div style={{ display: 'flex', gap: '8px', marginTop: '16px' }}>
              <button
                onClick={() => { setGenerated(false); setPreset(null); }}
                style={{ ...s.actionBtn('#2a2a2a'), flex: 1, justifyContent: 'center' }}
              >
                Back
              </button>
              <button
                onClick={sendEmail}
                disabled={sending || !subject.trim() || !body.trim()}
                style={{ ...s.actionBtn('#27ae60'), flex: 2, justifyContent: 'center', opacity: sending || !subject.trim() || !body.trim() ? 0.5 : 1 }}
              >
                {sending ? <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} /> : <Send size={16} />}
                Send Email
              </button>
            </div>
          </>
        )}

        {!generated && !loading && error && (
          <p style={{ color: '#e74c3c', fontSize: '13px', marginTop: '12px' }}>{error}</p>
        )}
      </div>
    </div>
  );
}

// --- Posting Form Modal ---
const FIELD_TYPES = [
  { value: 'text', label: 'Short Text' },
  { value: 'textarea', label: 'Long Text' },
  { value: 'select', label: 'Dropdown' },
  { value: 'number', label: 'Number' },
  { value: 'url', label: 'URL' },
  { value: 'yes_no', label: 'Yes / No' },
];

function PostingModal({ posting, onClose, onSaved, onTourTrigger, tourBoostZ }) {
  const [form, setForm] = useState({
    title: posting?.title || '',
    role_type: posting?.role_type || 'affiliate',
    description: posting?.description || '',
    requirements: posting?.requirements || '',
    compensation_summary: posting?.compensation_summary || '',
    slug: posting?.slug || '',
    status: posting?.status || 'active',
  });
  const [customFields, setCustomFields] = useState(posting?.custom_fields || []);
  const [saving, setSaving] = useState(false);
  const [slugEdited, setSlugEdited] = useState(!!posting);

  const addCustomField = () => {
    setCustomFields(prev => [...prev, { id: Date.now().toString(), label: '', type: 'text', required: false, options: '' }]);
  };

  const updateField = (id, key, val) => {
    setCustomFields(prev => prev.map(f => f.id === id ? { ...f, [key]: val } : f));
  };

  const removeField = (id) => {
    setCustomFields(prev => prev.filter(f => f.id !== id));
  };

  const handleTitleChange = (val) => {
    setForm(f => ({
      ...f,
      title: val,
      slug: slugEdited ? f.slug : slugify(val)
    }));
    if (val.trim().length >= 3 && onTourTrigger) onTourTrigger('posting_title_filled');
  };

  const save = async () => {
    if (!form.title.trim() || !form.slug.trim()) return;
    setSaving(true);
    try {
      // Clean custom fields — remove empty labels, parse options
      const cleanFields = customFields
        .filter(f => f.label.trim())
        .map(f => ({
          id: f.id,
          label: f.label.trim(),
          type: f.type,
          required: f.required,
          options: f.type === 'select' ? f.options.split(',').map(o => o.trim()).filter(Boolean) : undefined
        }));
      const payload = { ...form, custom_fields: cleanFields, created_by: getAdminEmail() };
      if (posting) {
        delete payload.created_by;
        await supabase.from('recruitment_postings').update(payload).eq('id', posting.id);
      } else {
        await supabase.from('recruitment_postings').insert(payload);
      }
      if (onTourTrigger) onTourTrigger('posting_saved');
      onSaved();
    } catch (err) {
      alert('Error saving: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ ...s.modal, zIndex: tourBoostZ ? 10001 : 100, background: tourBoostZ ? 'transparent' : s.modal.background }} onClick={onClose}>
      <div style={s.modalFull} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
          <h3 style={{ margin: 0, color: '#fff', fontSize: '17px' }}>{posting ? 'Edit Posting' : 'New Posting'}</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '8px' }}>
            <X size={20} color="#999" />
          </button>
        </div>

        <label style={s.label}>Title *</label>
        <input data-tour-id="posting-title" style={s.input} value={form.title} onChange={(e) => handleTitleChange(e.target.value)} placeholder="e.g. Regional Sales Director" />

        <label style={s.label}>Role Type *</label>
        <select style={s.select} value={form.role_type} onChange={(e) => setForm(f => ({ ...f, role_type: e.target.value }))}>
          <option value="director">Director</option>
          <option value="recruiter">Recruiter</option>
          <option value="affiliate">Affiliate</option>
        </select>

        <label style={s.label}>Description</label>
        <textarea style={s.textarea} value={form.description} onChange={(e) => setForm(f => ({ ...f, description: e.target.value }))} placeholder="What the role involves..." />

        <label style={s.label}>Requirements</label>
        <textarea style={{ ...s.textarea, minHeight: '80px' }} value={form.requirements} onChange={(e) => setForm(f => ({ ...f, requirements: e.target.value }))} placeholder="Required qualifications..." />

        <label style={s.label}>Compensation Summary</label>
        <input style={s.input} value={form.compensation_summary} onChange={(e) => setForm(f => ({ ...f, compensation_summary: e.target.value }))} placeholder="e.g. 25% recurring commission + bonuses" />

        {/* Custom Application Fields */}
        <div style={{ marginTop: '16px', borderTop: '1px solid #2a2a2a', paddingTop: '12px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
            <label style={{ ...s.label, margin: 0 }}>Custom Application Fields</label>
            <button
              type="button"
              onClick={addCustomField}
              style={{
                background: '#2a2a2a', border: 'none', cursor: 'pointer', padding: '6px 12px',
                borderRadius: '6px', display: 'flex', alignItems: 'center', gap: '4px',
                fontSize: '12px', color: '#ff6b35', fontWeight: '600'
              }}
            >
              <Plus size={14} /> Add Field
            </button>
          </div>
          <p style={{ fontSize: '11px', color: '#666', margin: '0 0 8px' }}>
            Name, email, and phone are always included. Add extra questions here.
          </p>

          {customFields.map((field, idx) => (
            <div key={field.id} style={{
              background: '#0a0a0a', borderRadius: '8px', padding: '10px', marginBottom: '8px',
              border: '1px solid #2a2a2a'
            }}>
              <div style={{ display: 'flex', gap: '6px', marginBottom: '6px' }}>
                <input
                  style={{ ...s.input, flex: 1, padding: '8px', fontSize: '13px' }}
                  placeholder="Field label (e.g. Years of sales experience)"
                  value={field.label}
                  onChange={(e) => updateField(field.id, 'label', e.target.value)}
                />
                <select
                  style={{ ...s.select, width: 'auto', padding: '8px', fontSize: '13px', minWidth: '110px' }}
                  value={field.type}
                  onChange={(e) => updateField(field.id, 'type', e.target.value)}
                >
                  {FIELD_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
                <button
                  onClick={() => removeField(field.id)}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px', color: '#e74c3c', flexShrink: 0 }}
                >
                  <Trash2 size={16} />
                </button>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '12px', color: '#aaa', cursor: 'pointer' }}>
                  <input type="checkbox" checked={field.required} onChange={(e) => updateField(field.id, 'required', e.target.checked)} style={{ accentColor: '#ff6b35' }} />
                  Required
                </label>
                {field.type === 'select' && (
                  <input
                    style={{ ...s.input, flex: 1, padding: '6px 8px', fontSize: '12px' }}
                    placeholder="Options (comma separated)"
                    value={field.options || ''}
                    onChange={(e) => updateField(field.id, 'options', e.target.value)}
                  />
                )}
              </div>
            </div>
          ))}
        </div>

        <label style={s.label}>URL Slug *</label>
        <input
          style={s.input}
          value={form.slug}
          onChange={(e) => { setSlugEdited(true); setForm(f => ({ ...f, slug: slugify(e.target.value) })); }}
          placeholder="e.g. regional-sales-director"
        />
        <p style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
          URL: https://workvanapp.com/apply?posting={form.slug || '...'}
        </p>

        <label style={s.label}>Status</label>
        <div style={{ display: 'flex', gap: '8px', marginTop: '4px' }}>
          {['active', 'paused', 'closed'].map(st => (
            <button
              key={st}
              onClick={() => setForm(f => ({ ...f, status: st }))}
              style={{
                padding: '8px 16px', borderRadius: '8px', border: '1px solid',
                borderColor: form.status === st ? '#ff6b35' : '#2a2a2a',
                background: form.status === st ? '#ff6b3522' : '#0a0a0a',
                color: form.status === st ? '#ff6b35' : '#888',
                fontSize: '13px', fontWeight: 600, cursor: 'pointer', textTransform: 'capitalize'
              }}
            >
              {st}
            </button>
          ))}
        </div>

        <button
          data-tour-id="posting-save-btn"
          onClick={save}
          disabled={saving || !form.title.trim() || !form.slug.trim()}
          style={{ ...s.primaryBtn, opacity: saving || !form.title.trim() || !form.slug.trim() ? 0.5 : 1 }}
        >
          {saving ? 'Saving...' : (posting ? 'Update Posting' : 'Create Posting')}
        </button>
      </div>
    </div>
  );
}


// --- Spotlight Tour (matches main app GuidedTour pattern) ---
// advance: 'click-target' = user must click the highlighted element
// advance: 'next-button' = user clicks Next in the tooltip
// target: CSS selector for the spotlight element (uses data-tour-id attributes)
const TOUR_STEPS = [
  {
    title: 'Welcome to Recruitment',
    content: 'Let\'s set up your first job posting together. Click through each step — I\'ll highlight exactly what to tap.',
    target: null,
    position: 'center',
    advance: 'next-button'
  },
  {
    title: 'Switch to Postings',
    content: 'Tap the Postings tab to see where you create job listings.',
    target: '[data-tour-id="tab-postings"]',
    position: 'bottom',
    advance: 'click-target'
  },
  {
    title: 'Create a Posting',
    content: 'Tap "New Posting" to create your first job listing.',
    target: '[data-tour-id="new-posting-btn"]',
    position: 'bottom',
    advance: 'click-target'
  },
  {
    title: 'Give It a Title',
    content: 'Type a title — this is what applicants see. Something like "Sales Director — Work Van Partner Program".',
    target: '[data-tour-id="posting-title"]',
    position: 'bottom',
    advance: 'click-target'
  },
  {
    title: 'Fill in the Details',
    content: 'Pick a role type, add a description, and a compensation summary (e.g., "25% recurring commission + bonuses"). The URL slug auto-generates from the title. Hit Next when you\'re ready to save.',
    target: null,
    position: 'center',
    advance: 'next-button',
    dimLevel: 0.3
  },
  {
    title: 'Save Your Posting',
    content: 'Hit "Create Posting" to save. You\'ll get a shareable application URL instantly.',
    target: '[data-tour-id="posting-save-btn"]',
    position: 'top',
    advance: 'callback',
    callbackId: 'posting_saved'
  },
  {
    title: 'Your Application Link',
    content: 'Your posting has a live application form! Here\'s the workflow: create your ad on Craigslist or LinkedIn as usual, then paste this link as the "apply here" URL. When someone clicks it, they see a clean application form. Once they submit, they land in your Pipeline automatically.',
    target: null,
    position: 'center',
    advance: 'next-button',
    dimLevel: 0.3
  },
  {
    title: 'Check the Pipeline',
    content: 'Now switch to Pipeline — this is where applicants land after they apply.',
    target: '[data-tour-id="tab-pipeline"]',
    position: 'bottom',
    advance: 'click-target'
  },
  {
    title: 'Your Pipeline',
    content: 'Filter by stage, search by name, and tap any card to expand it. The red "Needs Attention" pill flags anyone not contacted in 24+ hours.',
    target: null,
    position: 'center',
    advance: 'next-button'
  },
  {
    title: 'Your AI Assistant',
    content: 'This orange mic button is your hands-free AI. Tap and talk: "Pipeline summary", "Move John to interview", "Who needs attention?" Works while driving.',
    target: '[data-tour-id="voice-btn"]',
    position: 'left',
    advance: 'next-button'
  },
  {
    title: 'You\'re all set!',
    content: 'Share your posting link, watch applicants roll in, and manage everything from your phone.',
    target: null,
    position: 'center',
    advance: 'next-button'
  }
];

// ========== MAIN COMPONENT ==========
export default function Recruitment() {
  const [activeTab, setActiveTab] = useState('pipeline');

  // Pipeline state
  const [applicants, setApplicants] = useState([]);
  const [stageFilter, setStageFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedId, setExpandedId] = useState(null);
  const [activities, setActivities] = useState({});
  const [loadingActivities, setLoadingActivities] = useState({});
  const [loading, setLoading] = useState(true);
  const [stageCounts, setStageCounts] = useState({});

  // Note state
  const [noteOpenFor, setNoteOpenFor] = useState(null);
  const [savingNote, setSavingNote] = useState(false);

  // Email modal
  const [emailApplicant, setEmailApplicant] = useState(null);

  // Messaging modal (full email + SMS + templates)
  const [messagingApplicant, setMessagingApplicant] = useState(null);

  // Confirm reject
  const [confirmRejectId, setConfirmRejectId] = useState(null);

  // Staleness tracking
  const [staleMap, setStaleMap] = useState({}); // applicantId -> hours since last contact

  // Voice assistant
  const [voiceOpen, setVoiceOpen] = useState(false);
  const [voiceListening, setVoiceListening] = useState(false);
  const [voiceProcessing, setVoiceProcessing] = useState(false);
  const [voiceTranscript, setVoiceTranscript] = useState('');
  const [voiceReply, setVoiceReply] = useState('');
  const [voiceActions, setVoiceActions] = useState([]);
  const [voiceConfirm, setVoiceConfirm] = useState(false);
  const [voiceHistory, setVoiceHistory] = useState([]);
  const voiceRecRef = useRef(null);

  // Spotlight Tour
  const [tourStep, setTourStep] = useState(-1);
  const [tourDismissed, setTourDismissed] = useState(() => {
    try { return localStorage.getItem('recruitment_tour_done') === 'true'; } catch { return false; }
  });
  const [targetRect, setTargetRect] = useState(null);
  const [tooltipPos, setTooltipPos] = useState({});
  const tourPollRef = useRef(null);

  const tourActive = tourStep >= 0 && tourStep < TOUR_STEPS.length;
  const currentTourStep = tourActive ? TOUR_STEPS[tourStep] : null;

  const advanceTour = useCallback(() => {
    const next = tourStep + 1;
    if (next >= TOUR_STEPS.length) {
      setTourStep(-1);
      setTourDismissed(true);
      try { localStorage.setItem('recruitment_tour_done', 'true'); } catch {}
    } else {
      setTourStep(next);
    }
  }, [tourStep]);

  const skipTour = () => {
    setTourStep(-1);
    setTourDismissed(true);
    try { localStorage.setItem('recruitment_tour_done', 'true'); } catch {}
  };

  // Find target element and position tooltip
  const positionTourTooltip = useCallback(() => {
    if (!currentTourStep) return;
    if (!currentTourStep.target) {
      setTargetRect(null);
      setTooltipPos({ position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', zIndex: 10001 });
      return;
    }
    const el = document.querySelector(currentTourStep.target);
    if (!el) {
      // Target element isn't on the page right now (e.g. it was conditionally
      // hidden, or a modal closed). Don't let the tour get stuck on a stale
      // tooltip position — fall back to center placement so the Next button
      // is always reachable.
      setTargetRect(null);
      setTooltipPos({ position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', zIndex: 10001 });
      return;
    }

    const rect = el.getBoundingClientRect();
    setTargetRect(rect);

    const tw = Math.min(320, window.innerWidth - 32);
    const th = 180;
    const pad = 16;
    const pos = currentTourStep.position || 'bottom';
    let st = { position: 'fixed', zIndex: 10001, width: tw + 'px' };

    if (pos === 'bottom') {
      st.top = rect.bottom + pad;
      st.left = Math.max(pad, Math.min(rect.left + rect.width / 2 - tw / 2, window.innerWidth - pad - tw));
      if (st.top + th > window.innerHeight) { st.top = rect.top - th - pad; }
    } else if (pos === 'top') {
      st.top = rect.top - th - pad;
      st.left = Math.max(pad, Math.min(rect.left + rect.width / 2 - tw / 2, window.innerWidth - pad - tw));
      if (st.top < pad) { st.top = rect.bottom + pad; }
    } else if (pos === 'right') {
      st.top = Math.max(pad, rect.top + rect.height / 2 - th / 2);
      st.left = rect.right + pad;
      if (st.left + tw > window.innerWidth) { st.left = rect.left - tw - pad; }
    } else if (pos === 'left') {
      st.top = Math.max(pad, rect.top + rect.height / 2 - th / 2);
      st.left = rect.left - tw - pad;
      if (st.left < pad) { st.left = rect.right + pad; }
    }

    if (typeof st.top === 'number') st.top = st.top + 'px';
    if (typeof st.left === 'number') st.left = st.left + 'px';
    setTooltipPos(st);
  }, [currentTourStep]);

  // Poll for target + reposition
  useEffect(() => {
    if (!tourActive) return;
    positionTourTooltip();
    tourPollRef.current = setInterval(positionTourTooltip, 200);
    return () => clearInterval(tourPollRef.current);
  }, [tourActive, tourStep, positionTourTooltip]);

  // click-target: listen for clicks on the target element
  useEffect(() => {
    if (!tourActive || !currentTourStep || currentTourStep.advance !== 'click-target') return;
    if (!currentTourStep.target) return;

    let el = null;
    const handler = () => setTimeout(advanceTour, 150);
    const interval = setInterval(() => {
      el = document.querySelector(currentTourStep.target);
      if (el) { el.addEventListener('click', handler, { once: true }); clearInterval(interval); }
    }, 200);

    return () => { clearInterval(interval); if (el) el.removeEventListener('click', handler); };
  }, [tourActive, tourStep, currentTourStep, advanceTour]);

  // Callback trigger for PostingModal events
  const tourTrigger = (actionId) => {
    if (!tourActive) return;
    if (currentTourStep?.callbackId === actionId) advanceTour();
    // Title step uses click-target but also accept callback as fallback
    if (actionId === 'posting_title_filled' && currentTourStep?.target === '[data-tour-id="posting-title"]') advanceTour();
  };

  // Postings state
  const [postings, setPostings] = useState([]);
  const [postingCounts, setPostingCounts] = useState({});
  const [loadingPostings, setLoadingPostings] = useState(true);
  const [editingPosting, setEditingPosting] = useState(null);
  const [showArchived, setShowArchived] = useState(false);
  const [showNewPosting, setShowNewPosting] = useState(false);
  const [showNewApplicant, setShowNewApplicant] = useState(false);
  const [copiedSlug, setCopiedSlug] = useState(null);

  // --- Load Data ---
  const loadApplicants = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('recruitment_applicants')
        .select('*')
        .order('created_at', { ascending: false });
      if (error) throw error;
      setApplicants(data || []);
      // Compute stage counts
      const counts = {};
      (data || []).forEach(a => { counts[a.stage] = (counts[a.stage] || 0) + 1; });
      setStageCounts(counts);

      // Compute staleness for active-stage applicants
      const activeApplicants = (data || []).filter(a => ['new', 'screening', 'interview'].includes(a.stage));
      if (activeApplicants.length > 0) {
        const { data: activityData } = await supabase
          .from('recruitment_activity')
          .select('applicant_id, created_at')
          .in('applicant_id', activeApplicants.map(a => a.id))
          .order('created_at', { ascending: false });

        const latestActivity = {};
        (activityData || []).forEach(act => {
          if (!latestActivity[act.applicant_id]) latestActivity[act.applicant_id] = act.created_at;
        });

        const nowMs = Date.now();
        const newStaleMap = {};
        activeApplicants.forEach(a => {
          const lastContact = latestActivity[a.id] || a.created_at;
          const hoursSince = Math.floor((nowMs - new Date(lastContact).getTime()) / 3600000);
          if (hoursSince >= 24) newStaleMap[a.id] = hoursSince;
        });
        setStaleMap(newStaleMap);
      }
    } catch (err) {
      console.error('Failed to load applicants:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadPostings = useCallback(async () => {
    setLoadingPostings(true);
    try {
      const { data, error } = await supabase
        .from('recruitment_postings')
        .select('*')
        .order('created_at', { ascending: false });
      if (error) throw error;
      setPostings(data || []);
      // Get applicant counts per posting
      const { data: counts } = await supabase
        .from('recruitment_applicants')
        .select('posting_id');
      const map = {};
      (counts || []).forEach(c => { map[c.posting_id] = (map[c.posting_id] || 0) + 1; });
      setPostingCounts(map);
    } catch (err) {
      console.error('Failed to load postings:', err);
    } finally {
      setLoadingPostings(false);
    }
  }, []);

  const loadActivities = async (applicantId) => {
    if (activities[applicantId]) return;
    setLoadingActivities(prev => ({ ...prev, [applicantId]: true }));
    try {
      const { data } = await supabase
        .from('recruitment_activity')
        .select('*')
        .eq('applicant_id', applicantId)
        .order('created_at', { ascending: false });
      setActivities(prev => ({ ...prev, [applicantId]: data || [] }));
    } catch (err) {
      console.error('Failed to load activities:', err);
    } finally {
      setLoadingActivities(prev => ({ ...prev, [applicantId]: false }));
    }
  };

  useEffect(() => {
    loadApplicants();
    loadPostings();
  }, [loadApplicants, loadPostings]);

  // Start tour on first visit
  useEffect(() => {
    if (!tourDismissed && !loading) {
      const timer = setTimeout(() => setTourStep(0), 800);
      return () => clearTimeout(timer);
    }
  }, [tourDismissed, loading]); // eslint-disable-line

  // --- Actions ---
  const updateRating = async (id, rating) => {
    setApplicants(prev => prev.map(a => a.id === id ? { ...a, rating } : a));
    await supabase.from('recruitment_applicants').update({ rating }).eq('id', id);
  };

  const advanceStage = async (applicant) => {
    const next = NEXT_STAGE[applicant.stage];
    if (!next) return;
    const now = new Date().toISOString();
    setApplicants(prev => prev.map(a => a.id === applicant.id ? { ...a, stage: next, updated_at: now } : a));
    setStageCounts(prev => ({
      ...prev,
      [applicant.stage]: (prev[applicant.stage] || 1) - 1,
      [next]: (prev[next] || 0) + 1
    }));
    await supabase.from('recruitment_applicants').update({ stage: next, updated_at: now }).eq('id', applicant.id);
    await supabase.from('recruitment_activity').insert({
      applicant_id: applicant.id,
      type: 'stage_change',
      content: `Stage changed from ${applicant.stage} to ${next}`,
      created_by: getAdminEmail()
    });
    // Refresh activities if loaded
    if (activities[applicant.id]) {
      setActivities(prev => ({ ...prev, [applicant.id]: undefined }));
      loadActivities(applicant.id);
    }
  };

  const rejectApplicant = async (applicant) => {
    const now = new Date().toISOString();
    const oldStage = applicant.stage;
    setApplicants(prev => prev.map(a => a.id === applicant.id ? { ...a, stage: 'rejected', updated_at: now } : a));
    setStageCounts(prev => ({
      ...prev,
      [oldStage]: (prev[oldStage] || 1) - 1,
      rejected: (prev.rejected || 0) + 1
    }));
    setConfirmRejectId(null);
    await supabase.from('recruitment_applicants').update({ stage: 'rejected', updated_at: now }).eq('id', applicant.id);
    await supabase.from('recruitment_activity').insert({
      applicant_id: applicant.id,
      type: 'stage_change',
      content: `Rejected (was ${oldStage})`,
      created_by: getAdminEmail()
    });
    if (activities[applicant.id]) {
      setActivities(prev => ({ ...prev, [applicant.id]: undefined }));
      loadActivities(applicant.id);
    }
  };

  const saveNote = async (applicantId, text, wasVoice) => {
    setSavingNote(true);
    try {
      await supabase.from('recruitment_activity').insert({
        applicant_id: applicantId,
        type: wasVoice ? 'voice_note' : 'note',
        content: text,
        created_by: getAdminEmail()
      });
      setNoteOpenFor(null);
      setActivities(prev => ({ ...prev, [applicantId]: undefined }));
      loadActivities(applicantId);
    } catch (err) {
      alert('Failed to save note: ' + err.message);
    } finally {
      setSavingNote(false);
    }
  };

  const toggleExpand = (id) => {
    if (expandedId === id) {
      setExpandedId(null);
      setNoteOpenFor(null);
    } else {
      setExpandedId(id);
      setNoteOpenFor(null);
      loadActivities(id);
    }
  };

  const copyToClipboard = (text, id) => {
    navigator.clipboard.writeText(text);
    setCopiedSlug(id);
    setTimeout(() => setCopiedSlug(null), 2000);
  };

  // --- Filtered applicants ---
  const filtered = applicants.filter(a => {
    if (stageFilter === 'stale') { if (!staleMap[a.id]) return false; }
    else if (stageFilter !== 'all' && a.stage !== stageFilter) return false;
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      return (a.name || '').toLowerCase().includes(q) || (a.email || '').toLowerCase().includes(q);
    }
    return true;
  });

  // --- Activity icon ---
  const activityIcon = (type) => {
    switch (type) {
      case 'stage_change': return <ArrowRight size={14} color="#f39c12" />;
      case 'email_sent': return <Mail size={14} color="#3498db" />;
      case 'note': return <FileText size={14} color="#888" />;
      case 'voice_note': return <Mic size={14} color="#e74c3c" />;
      case 'application_received': return <Users size={14} color="#27ae60" />;
      default: return <Clock size={14} color="#666" />;
    }
  };

  const statusBadge = (status) => {
    const c = status === 'active' ? '#27ae60' : status === 'paused' ? '#f39c12' : '#e74c3c';
    return <span style={s.badge(c)}>{status}</span>;
  };

  // --- Voice Assistant ---
  const staleApplicants = Object.entries(staleMap).map(([id, hrs]) => {
    const a = applicants.find(x => x.id === id);
    return a ? { id, name: a.name, stage: a.stage, hoursSinceContact: hrs } : null;
  }).filter(Boolean);

  const startVoiceListening = () => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) { alert('Speech recognition not supported'); return; }

    setVoiceOpen(true);
    setVoiceListening(true);
    setVoiceTranscript('');
    setVoiceReply('');
    setVoiceActions([]);
    setVoiceConfirm(false);

    const rec = new SpeechRecognition();
    rec.continuous = false;
    rec.interimResults = true;
    rec.lang = 'en-US';

    let finalText = '';
    rec.onresult = (event) => {
      let interim = '';
      for (let i = event.resultIndex; i < event.results.length; i++) {
        if (event.results[i].isFinal) finalText += event.results[i][0].transcript;
        else interim += event.results[i][0].transcript;
      }
      setVoiceTranscript(finalText + interim);
    };

    rec.onend = () => {
      setVoiceListening(false);
      if (finalText.trim()) processVoiceCommand(finalText.trim());
      else setVoiceReply('I didn\'t catch that. Tap the mic to try again.');
    };

    rec.onerror = () => { setVoiceListening(false); setVoiceReply('Mic error. Try again.'); };
    voiceRecRef.current = rec;
    rec.start();
  };

  const stopVoiceListening = () => {
    voiceRecRef.current?.stop();
    setVoiceListening(false);
  };

  const processVoiceCommand = async (text) => {
    setVoiceProcessing(true);
    try {
      const res = await fetch('https://workvanapp.com/api/recruitment-voice', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          transcript: text,
          pipeline: {
            applicants: applicants.map(a => ({ id: a.id, name: a.name, email: a.email, phone: a.phone, stage: a.stage, rating: a.rating, role_interest: a.role_interest, city: a.city, state: a.state })),
            stageCounts,
            staleApplicants
          },
          conversationHistory: voiceHistory.slice(-8)
        })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);

      setVoiceReply(data.reply);
      setVoiceActions(data.actions || []);
      setVoiceConfirm(data.confirmRequired || false);
      setVoiceHistory(prev => [...prev, { role: 'user', content: text }, { role: 'assistant', content: data.reply }]);

      // Speak the reply
      if (data.reply && window.speechSynthesis) {
        const utterance = new SpeechSynthesisUtterance(data.reply);
        utterance.rate = 1.1;
        utterance.pitch = 1;
        window.speechSynthesis.speak(utterance);
      }

      // Auto-execute non-destructive actions
      if (!data.confirmRequired && data.actions?.length > 0) {
        await executeVoiceActions(data.actions);
      }
    } catch (err) {
      setVoiceReply('Sorry, something went wrong. Try again.');
    } finally {
      setVoiceProcessing(false);
    }
  };

  const executeVoiceActions = async (actions) => {
    for (const action of actions) {
      const applicant = applicants.find(a => a.id === action.applicantId);
      if (!applicant && action.type !== 'filter_stage') continue;

      switch (action.type) {
        case 'advance_stage':
          await advanceStage(applicant);
          break;
        case 'reject':
          await rejectApplicant(applicant);
          break;
        case 'add_note':
          await saveNote(applicant.id, action.content, false);
          break;
        case 'draft_email':
          setEmailApplicant(applicant);
          break;
        case 'filter_stage':
          if (action.stage) setStageFilter(action.stage);
          break;
        default:
          break;
      }
    }
    setVoiceActions([]);
    setVoiceConfirm(false);
    loadApplicants();
  };

  const dismissVoice = () => {
    setVoiceOpen(false);
    setVoiceListening(false);
    setVoiceProcessing(false);
    setVoiceTranscript('');
    setVoiceReply('');
    setVoiceActions([]);
    voiceRecRef.current?.stop();
    window.speechSynthesis?.cancel();
  };

  // ========== RENDER ==========
  return (
    <div style={s.page}>
      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes pulse { 0%,100% { opacity: 1; } 50% { opacity: 0.5; } }
        input::placeholder, textarea::placeholder { color: #555; }
        select option { background: #0a0a0a; color: #e0e0e0; }
        ::-webkit-scrollbar { width: 4px; height: 4px; }
        ::-webkit-scrollbar-thumb { background: #333; border-radius: 4px; }
      `}</style>

      {/* Header */}
      <div style={s.header}>
        <h1 style={s.title}>Recruitment</h1>
        <div style={s.tabs}>
          <button data-tour-id="tab-pipeline" style={s.tab(activeTab === 'pipeline')} onClick={() => { setActiveTab('pipeline'); tourTrigger('tab_pipeline'); }}>Pipeline</button>
          <button data-tour-id="tab-postings" style={s.tab(activeTab === 'postings')} onClick={() => { setActiveTab('postings'); tourTrigger('tab_postings'); }}>Postings</button>
        </div>
        <button
          onClick={() => setShowNewApplicant(true)}
          style={{
            marginLeft: 'auto',
            padding: '0.6rem 1rem',
            background: '#ff6b35',
            border: 'none',
            borderRadius: '8px',
            color: '#fff',
            fontWeight: 600,
            cursor: 'pointer',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '0.4rem',
          }}
        >
          + New Applicant
        </button>
      </div>

      {/* ========== PIPELINE TAB ========== */}
      {activeTab === 'pipeline' && (
        <div>
          {/* Stage filters */}
          <div style={s.pillBar}>
            <button style={s.pill(stageFilter === 'all')} onClick={() => setStageFilter('all')}>
              All <span style={{ fontSize: '11px', opacity: 0.7 }}>({applicants.length})</span>
            </button>
            {Object.keys(staleMap).length > 0 && (
              <button style={{ ...s.pill(stageFilter === 'stale'), background: stageFilter === 'stale' ? '#e74c3c' : '#2a1a1a', borderColor: stageFilter === 'stale' ? '#e74c3c' : '#e74c3c44' }}
                onClick={() => setStageFilter('stale')}>
                <AlertTriangle size={13} color={stageFilter === 'stale' ? '#fff' : '#e74c3c'} />
                <span style={{ color: stageFilter === 'stale' ? '#fff' : '#e74c3c' }}>Needs Attention</span>
                <span style={{ background: '#e74c3c', color: '#fff', fontSize: '11px', padding: '1px 6px', borderRadius: '10px', fontWeight: 700 }}>
                  {Object.keys(staleMap).length}
                </span>
              </button>
            )}
            {STAGES.map(st => (
              <button key={st} style={s.pill(stageFilter === st)} onClick={() => setStageFilter(st)}>
                <span style={{ textTransform: 'capitalize' }}>{st}</span>
                {(stageCounts[st] || 0) > 0 && (
                  <span style={{
                    background: stageFilter === st ? '#fff3' : STAGE_COLORS[st] + '44',
                    color: stageFilter === st ? '#fff' : STAGE_COLORS[st],
                    fontSize: '11px', padding: '1px 6px', borderRadius: '10px', fontWeight: 700
                  }}>
                    {stageCounts[st]}
                  </span>
                )}
              </button>
            ))}
          </div>

          {/* Search */}
          <div style={s.searchWrap}>
            <div style={{ position: 'relative' }}>
              <Search size={16} color="#666" style={{ position: 'absolute', left: '12px', top: '12px' }} />
              <input
                style={s.searchInput}
                placeholder="Search by name or email..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  style={{ position: 'absolute', right: '8px', top: '8px', background: 'none', border: 'none', cursor: 'pointer', padding: '4px' }}
                >
                  <X size={16} color="#666" />
                </button>
              )}
            </div>
          </div>

          {/* Loading */}
          {loading && (
            <div style={{ textAlign: 'center', padding: '40px 0' }}>
              <Loader2 size={24} color="#ff6b35" style={{ animation: 'spin 1s linear infinite' }} />
            </div>
          )}

          {/* Empty state */}
          {!loading && filtered.length === 0 && (
            <div style={s.emptyState}>
              <Users size={40} color="#333" style={{ marginBottom: '12px' }} />
              <p style={{ margin: 0 }}>{searchQuery || stageFilter !== 'all' ? 'No applicants match your filters' : 'No applicants yet'}</p>
            </div>
          )}

          {/* Applicant cards */}
          {!loading && filtered.map(applicant => {
            const isExpanded = expandedId === applicant.id;
            const acts = activities[applicant.id] || [];
            const loadingActs = loadingActivities[applicant.id];

            const hoursStale = staleMap[applicant.id];
            const isStale = hoursStale >= 24;
            const isCritical = hoursStale >= 48;

            return (
              <div
                key={applicant.id}
                style={{
                  ...s.card,
                  borderColor: isExpanded ? '#ff6b35' : isStale ? (isCritical ? '#e74c3c' : '#f39c12') : '#2a2a2a',
                  borderLeft: isStale ? `3px solid ${isCritical ? '#e74c3c' : '#f39c12'}` : undefined
                }}
                onClick={() => toggleExpand(applicant.id)}
              >
                {/* Card header */}
                <div style={s.cardRow}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                      <span style={s.name}>{applicant.name}</span>
                      <span style={s.badge(ROLE_COLORS[applicant.role_interest] || '#888')}>
                        {applicant.role_interest || 'N/A'}
                      </span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '4px', flexWrap: 'wrap' }}>
                      {(applicant.city || applicant.state) && (
                        <span style={{ ...s.subText, display: 'flex', alignItems: 'center', gap: '3px' }}>
                          <MapPin size={12} /> {[applicant.city, applicant.state].filter(Boolean).join(', ')}
                        </span>
                      )}
                      <span style={s.badge(STAGE_COLORS[applicant.stage])}>
                        {applicant.stage}
                      </span>
                      <span style={{ fontSize: '12px', color: '#666' }}>{relativeTime(applicant.created_at)}</span>
                      {isStale && (
                        <span style={{ fontSize: '11px', color: isCritical ? '#e74c3c' : '#f39c12', display: 'flex', alignItems: 'center', gap: '3px' }}>
                          <AlertTriangle size={11} /> {Math.floor(hoursStale / 24)}d no contact
                        </span>
                      )}
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    {isExpanded ? <ChevronDown size={18} color="#666" /> : <ChevronRight size={18} color="#666" />}
                  </div>
                </div>

                {/* Rating row */}
                <div style={{ marginTop: '8px' }}>
                  <StarRating rating={applicant.rating || 0} onRate={(r) => updateRating(applicant.id, r)} />
                </div>

                {/* Expanded content */}
                {isExpanded && (
                  <div style={s.expandedSection} onClick={(e) => e.stopPropagation()}>
                    {/* Contact info */}
                    <div>
                      {applicant.email && (
                        <div style={s.infoRow}>
                          <Mail size={14} color="#888" />
                          <a href={`mailto:${applicant.email}`} style={{ color: '#3498db', textDecoration: 'none', wordBreak: 'break-all' }}>
                            {applicant.email}
                          </a>
                          <button
                            onClick={() => copyToClipboard(applicant.email, applicant.id + '-email')}
                            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px' }}
                          >
                            {copiedSlug === applicant.id + '-email' ? <Check size={14} color="#27ae60" /> : <Copy size={14} color="#666" />}
                          </button>
                        </div>
                      )}
                      {applicant.phone && (
                        <div style={s.infoRow}>
                          <Phone size={14} color="#888" />
                          <a href={`tel:${applicant.phone}`} style={{ color: '#4ecca3', textDecoration: 'none' }}>
                            {applicant.phone}
                          </a>
                        </div>
                      )}
                      {applicant.linkedin_url && (
                        <div style={s.infoRow}>
                          <Globe size={14} color="#888" />
                          <a href={applicant.linkedin_url} target="_blank" rel="noopener noreferrer" style={{ color: '#3498db', textDecoration: 'none', wordBreak: 'break-all' }}>
                            LinkedIn Profile
                          </a>
                          <ExternalLink size={12} color="#666" />
                        </div>
                      )}
                      {applicant.resume_url && (
                        <div style={s.infoRow}>
                          <FileText size={14} color="#888" />
                          <a href={applicant.resume_url} target="_blank" rel="noopener noreferrer" style={{ color: '#3498db', textDecoration: 'none' }}>
                            View Resume
                          </a>
                          <ExternalLink size={12} color="#666" />
                        </div>
                      )}
                      {applicant.source && (
                        <div style={s.infoRow}>
                          <Link size={14} color="#888" />
                          <span style={{ color: '#aaa' }}>Source: {applicant.source}</span>
                        </div>
                      )}
                    </div>

                    {/* Experience summary */}
                    {applicant.experience_summary && (
                      <div style={{ marginTop: '10px', padding: '10px', background: '#0a0a0a', borderRadius: '8px', fontSize: '13px', color: '#ccc', lineHeight: '1.5' }}>
                        {applicant.experience_summary}
                      </div>
                    )}

                    {/* Custom field answers */}
                    {applicant.custom_answers && Object.keys(applicant.custom_answers).length > 0 && (
                      <div style={{ marginTop: '8px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                        {Object.entries(applicant.custom_answers).map(([fieldId, answer]) => (
                          <div key={fieldId} style={{ fontSize: '13px', color: '#aaa' }}>
                            <span style={{ color: '#888' }}>{fieldId}: </span>
                            <span style={{ color: '#ccc' }}>{answer}</span>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Quick actions */}
                    <div style={s.actionBar}>
                      {applicant.stage !== 'hired' && applicant.stage !== 'rejected' && NEXT_STAGE[applicant.stage] && (
                        <button style={s.actionBtn('#27ae60')} onClick={() => advanceStage(applicant)}>
                          <ArrowRight size={16} />
                          Move to {NEXT_STAGE[applicant.stage].charAt(0).toUpperCase() + NEXT_STAGE[applicant.stage].slice(1)}
                        </button>
                      )}
                      {(applicant.email || applicant.phone) && (
                        <button style={s.actionBtn('#ff6b35')} onClick={() => setMessagingApplicant(applicant)}>
                          <Send size={16} /> Message
                        </button>
                      )}
                      {applicant.email && (
                        <button style={s.actionBtn('#3498db')} onClick={() => setEmailApplicant(applicant)}>
                          <Mail size={16} /> AI Email
                        </button>
                      )}
                      <button
                        style={s.actionBtn('#555')}
                        onClick={() => setNoteOpenFor(noteOpenFor === applicant.id ? null : applicant.id)}
                      >
                        <MessageSquare size={16} /> Note
                      </button>
                      {applicant.stage !== 'rejected' && (
                        confirmRejectId === applicant.id ? (
                          <div style={{ display: 'flex', gap: '6px', flexShrink: 0 }}>
                            <button style={s.actionBtn('#e74c3c')} onClick={() => rejectApplicant(applicant)}>
                              Confirm
                            </button>
                            <button style={s.actionBtn('#2a2a2a')} onClick={() => setConfirmRejectId(null)}>
                              Cancel
                            </button>
                          </div>
                        ) : (
                          <button style={s.actionBtn('#e74c3c')} onClick={() => setConfirmRejectId(applicant.id)}>
                            <X size={16} /> Reject
                          </button>
                        )
                      )}
                      {applicant.phone && (
                        <a href={`tel:${applicant.phone}`} style={{ ...s.actionBtn('#0d9488'), textDecoration: 'none' }}>
                          <Phone size={16} /> Call
                        </a>
                      )}
                    </div>

                    {/* Voice note input */}
                    {noteOpenFor === applicant.id && (
                      <VoiceNoteInput
                        onSave={(text, wasVoice) => saveNote(applicant.id, text, wasVoice)}
                        onCancel={() => setNoteOpenFor(null)}
                        saving={savingNote}
                      />
                    )}

                    {/* Activity feed */}
                    <div style={{ marginTop: '16px' }}>
                      <p style={{ fontSize: '13px', fontWeight: 600, color: '#888', marginBottom: '8px' }}>Activity</p>
                      {loadingActs && (
                        <div style={{ textAlign: 'center', padding: '12px 0' }}>
                          <Loader2 size={16} color="#666" style={{ animation: 'spin 1s linear infinite' }} />
                        </div>
                      )}
                      {!loadingActs && acts.length === 0 && (
                        <p style={{ fontSize: '13px', color: '#555', fontStyle: 'italic' }}>No activity yet</p>
                      )}
                      {!loadingActs && acts.map(act => (
                        <div key={act.id} style={s.activityItem}>
                          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                            <div style={{ marginTop: '2px', flexShrink: 0 }}>{activityIcon(act.type)}</div>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <span style={{ fontSize: '12px', color: '#666', textTransform: 'capitalize' }}>
                                  {act.type.replace(/_/g, ' ')}
                                </span>
                                <span style={{ fontSize: '11px', color: '#555' }}>{relativeTime(act.created_at)}</span>
                              </div>
                              <p style={{ margin: '2px 0 0', fontSize: '13px', color: '#bbb', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                {act.content}
                              </p>
                              {act.created_by && (
                                <span style={{ fontSize: '11px', color: '#555' }}>by {act.created_by}</span>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })}

          {/* Bottom spacing for mobile */}
          <div style={{ height: '80px' }} />
        </div>
      )}

      {/* ========== POSTINGS TAB ========== */}
      {activeTab === 'postings' && (
        <div>
          {/* Add button */}
          <div style={{ padding: '12px 16px' }}>
            <button
              data-tour-id="new-posting-btn"
              onClick={() => { setShowNewPosting(true); tourTrigger('new_posting_click'); }}
              style={{ ...s.primaryBtn, marginTop: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}
            >
              <Plus size={18} /> New Posting
            </button>
          </div>

          {loadingPostings && (
            <div style={{ textAlign: 'center', padding: '40px 0' }}>
              <Loader2 size={24} color="#ff6b35" style={{ animation: 'spin 1s linear infinite' }} />
            </div>
          )}

          {!loadingPostings && postings.filter(p => p.status === 'archived').length > 0 && (
            <button
              onClick={() => setShowArchived(!showArchived)}
              style={{ background: 'none', border: 'none', color: '#888', fontSize: '13px', cursor: 'pointer', padding: '4px 0', marginBottom: '8px' }}
            >
              {showArchived ? 'Hide archived' : `Show ${postings.filter(p => p.status === 'archived').length} archived`}
            </button>
          )}

          {!loadingPostings && postings.filter(p => showArchived || p.status !== 'archived').length === 0 && (
            <div style={s.emptyState}>
              <Briefcase size={40} color="#333" style={{ marginBottom: '12px' }} />
              <p style={{ margin: 0 }}>No postings yet. Create one to start recruiting.</p>
            </div>
          )}

          {!loadingPostings && postings.filter(p => showArchived || p.status !== 'archived').map((posting, postingIdx) => {
            const url = `https://workvanapp.com/apply?posting=${posting.slug}`;
            return (
              <div key={posting.id} data-tour-id={postingIdx === 0 ? 'first-posting-card' : undefined} style={s.card}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                      <span style={s.name}>{posting.title}</span>
                      {statusBadge(posting.status)}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '6px', flexWrap: 'wrap' }}>
                      <span style={s.badge(ROLE_COLORS[posting.role_type] || '#888')}>
                        {posting.role_type}
                      </span>
                      <span style={{ fontSize: '13px', color: '#888' }}>
                        {postingCounts[posting.id] || 0} applicant{(postingCounts[posting.id] || 0) !== 1 ? 's' : ''}
                      </span>
                    </div>
                    {posting.compensation_summary && (
                      <p style={{ fontSize: '13px', color: '#aaa', marginTop: '6px', marginBottom: 0 }}>
                        {posting.compensation_summary}
                      </p>
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: '4px', flexShrink: 0 }}>
                    <button
                      onClick={() => setEditingPosting(posting)}
                      title="Edit"
                      style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '8px' }}
                    >
                      <Edit2 size={16} color="#888" />
                    </button>
                    <button
                      onClick={async () => {
                        const newStatus = posting.status === 'archived' ? 'active' : 'archived';
                        await supabase.from('recruitment_postings').update({ status: newStatus }).eq('id', posting.id);
                        loadPostings();
                      }}
                      title={posting.status === 'archived' ? 'Unarchive' : 'Archive'}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '8px' }}
                    >
                      <EyeOff size={16} color={posting.status === 'archived' ? '#f39c12' : '#888'} />
                    </button>
                    <button
                      onClick={async () => {
                        if (!window.confirm('Delete this posting? This cannot be undone.')) return;
                        await supabase.from('recruitment_postings').delete().eq('id', posting.id);
                        loadPostings();
                      }}
                      title="Delete"
                      style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '8px' }}
                    >
                      <Trash2 size={16} color="#e74c3c" />
                    </button>
                  </div>
                </div>

                {/* Application URL */}
                <div style={{
                  marginTop: '10px', padding: '8px 10px', background: '#0a0a0a', borderRadius: '6px',
                  display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap'
                }}>
                  <Link size={14} color="#666" style={{ flexShrink: 0 }} />
                  <a
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ fontSize: '12px', color: '#3498db', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', textDecoration: 'none' }}
                  >
                    {url}
                  </a>
                  <div style={{ display: 'flex', gap: '4px', flexShrink: 0 }}>
                    <button
                      onClick={() => copyToClipboard(url, posting.id)}
                      style={{
                        background: '#2a2a2a', border: 'none', cursor: 'pointer', padding: '6px 12px',
                        borderRadius: '6px', display: 'flex', alignItems: 'center', gap: '4px',
                        fontSize: '12px', color: '#ccc', minHeight: '32px'
                      }}
                    >
                      {copiedSlug === posting.id ? <Check size={14} color="#27ae60" /> : <Copy size={14} />}
                      {copiedSlug === posting.id ? 'Copied' : 'Copy Link'}
                    </button>
                    <a
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        background: '#2a2a2a', border: 'none', cursor: 'pointer', padding: '6px 12px',
                        borderRadius: '6px', display: 'flex', alignItems: 'center', gap: '4px',
                        fontSize: '12px', color: '#ccc', minHeight: '32px', textDecoration: 'none'
                      }}
                    >
                      <Eye size={14} /> Preview
                    </a>
                  </div>
                </div>
              </div>
            );
          })}

          <div style={{ height: '80px' }} />
        </div>
      )}

      {/* ========== MODALS ========== */}

      {/* Email Modal */}
      {emailApplicant && (
        <EmailModal
          applicant={emailApplicant}
          onClose={() => setEmailApplicant(null)}
          onSent={() => {
            setEmailApplicant(null);
            // Refresh activities for this applicant
            if (expandedId === emailApplicant.id) {
              setActivities(prev => ({ ...prev, [emailApplicant.id]: undefined }));
              loadActivities(emailApplicant.id);
            }
          }}
        />
      )}

      {/* Messaging Modal (Email + SMS + Templates) */}
      {messagingApplicant && (
        <ApplicantMessaging
          applicant={messagingApplicant}
          userEmail={getAdminEmail()}
          onClose={() => {
            setMessagingApplicant(null);
            // Refresh activities for this applicant
            if (expandedId === messagingApplicant.id) {
              setActivities(prev => ({ ...prev, [messagingApplicant.id]: undefined }));
              loadActivities(messagingApplicant.id);
            }
          }}
        />
      )}

      {/* Posting Create/Edit Modal */}
      {(showNewPosting || editingPosting) && (
        <PostingModal
          posting={editingPosting}
          onClose={() => { setShowNewPosting(false); setEditingPosting(null); }}
          onSaved={() => { setShowNewPosting(false); setEditingPosting(null); loadPostings(); }}
          onTourTrigger={tourActive ? tourTrigger : undefined}
          tourBoostZ={tourActive && currentTourStep?.target?.includes('posting-')}
        />
      )}

      {/* Spotlight Tour */}
      {tourActive && (
        <>
          <style>{`
            @keyframes tourPulseRing { 0% { transform: scale(1); opacity: 1; } 100% { transform: scale(3); opacity: 0; } }
            @keyframes tourTooltipIn { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }
          `}</style>

          {/* Overlay with spotlight cutout */}
          {!targetRect ? (
            /* No target — full overlay, pointer-events none so tooltip buttons work */
            <div style={{
              position: 'fixed', inset: 0, zIndex: 10000,
              background: `rgba(0,0,0,${currentTourStep.dimLevel ?? 0.7})`,
              pointerEvents: 'none'
            }} />
          ) : (
            <>
              {/* Spotlight hole via box-shadow */}
              <div style={{
                position: 'fixed',
                top: targetRect.top - 8,
                left: targetRect.left - 8,
                width: targetRect.width + 16,
                height: targetRect.height + 16,
                borderRadius: '8px',
                boxShadow: '0 0 0 9999px rgba(0,0,0,0.7)',
                zIndex: 10000,
                pointerEvents: 'none'
              }} />
              {/* Click-blocking regions around the spotlight */}
              <div style={{ position: 'fixed', top: 0, left: 0, right: 0, height: Math.max(0, targetRect.top - 8), zIndex: 10000, pointerEvents: 'auto' }} onClick={e => e.stopPropagation()} />
              <div style={{ position: 'fixed', bottom: 0, left: 0, right: 0, height: Math.max(0, window.innerHeight - targetRect.bottom - 8), zIndex: 10000, pointerEvents: 'auto' }} onClick={e => e.stopPropagation()} />
              <div style={{ position: 'fixed', top: targetRect.top - 8, left: 0, width: Math.max(0, targetRect.left - 8), height: targetRect.height + 16, zIndex: 10000, pointerEvents: 'auto' }} onClick={e => e.stopPropagation()} />
              <div style={{ position: 'fixed', top: targetRect.top - 8, left: targetRect.right + 8, right: 0, height: targetRect.height + 16, zIndex: 10000, pointerEvents: 'auto' }} onClick={e => e.stopPropagation()} />

              {/* Pulse ring on click-target steps */}
              {currentTourStep.advance === 'click-target' && (
                <div style={{
                  position: 'fixed',
                  top: targetRect.top + targetRect.height / 2 - 20,
                  left: targetRect.left + targetRect.width / 2 - 20,
                  width: '40px', height: '40px', borderRadius: '50%',
                  border: '3px solid #ff6b35',
                  zIndex: 10000, pointerEvents: 'none',
                  animation: 'tourPulseRing 1.5s ease-out infinite'
                }} />
              )}
            </>
          )}

          {/* Tooltip card */}
          {(tooltipPos.top || tooltipPos.transform) && (
            <div style={{
              ...tooltipPos,
              background: '#1a1a1a', borderRadius: '12px', padding: '20px',
              border: '1px solid #2a2a2a', boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
              maxWidth: '320px', animation: 'tourTooltipIn 0.3s ease-out'
            }}>
              {/* Progress dots */}
              <div style={{ display: 'flex', gap: '4px', marginBottom: '12px' }}>
                {TOUR_STEPS.map((_, i) => (
                  <div key={i} style={{
                    width: i === tourStep ? '20px' : '8px', height: '4px', borderRadius: '2px',
                    background: i === tourStep ? '#ff6b35' : '#2a2a2a', transition: 'all 0.3s'
                  }} />
                ))}
              </div>

              <h3 style={{ margin: '0 0 6px', fontSize: '16px', fontWeight: '700', color: '#fff' }}>
                {currentTourStep.title}
              </h3>
              <p style={{ margin: '0 0 16px', fontSize: '13px', color: '#aaa', lineHeight: '1.6' }}>
                {currentTourStep.content}
              </p>

              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <button onClick={skipTour} style={{
                  background: 'none', border: 'none', color: '#666', fontSize: '13px', cursor: 'pointer', padding: '6px 0'
                }}>
                  Skip tour
                </button>

                {currentTourStep.advance === 'next-button' && (
                  <button onClick={advanceTour} style={{
                    padding: '8px 20px', background: tourStep === TOUR_STEPS.length - 1 ? '#27ae60' : '#ff6b35',
                    border: 'none', borderRadius: '8px', color: '#fff', fontSize: '13px', fontWeight: '600', cursor: 'pointer'
                  }}>
                    {tourStep === TOUR_STEPS.length - 1 ? 'Done' : 'Next'}
                  </button>
                )}

                {(currentTourStep.advance === 'click-target' || currentTourStep.advance === 'callback') && (
                  <span style={{ fontSize: '12px', color: '#ff6b35', fontStyle: 'italic' }}>
                    Click the highlighted area
                  </span>
                )}
              </div>
            </div>
          )}
        </>
      )}

      {/* Voice Assistant Floating Button */}
      {!voiceOpen && (
        <button
          data-tour-id="voice-btn"
          onClick={startVoiceListening}
          style={{
            position: 'fixed', bottom: '24px', right: '24px', zIndex: 50,
            width: '56px', height: '56px', borderRadius: '50%',
            background: 'linear-gradient(135deg, #ff6b35, #f7931e)',
            border: 'none', cursor: 'pointer', boxShadow: '0 4px 20px rgba(255,107,53,0.4)',
            display: 'flex', alignItems: 'center', justifyContent: 'center'
          }}
          title="Voice Assistant"
        >
          <Mic size={24} color="#fff" />
        </button>
      )}

      {/* Voice Assistant Panel */}
      {voiceOpen && (
        <div style={{
          position: 'fixed', bottom: '24px', right: '24px', zIndex: 50,
          width: '320px', maxWidth: 'calc(100vw - 32px)',
          background: '#1a1a1a', border: '1px solid #2a2a2a', borderRadius: '16px',
          boxShadow: '0 8px 32px rgba(0,0,0,0.5)', overflow: 'hidden'
        }}>
          {/* Header */}
          <div style={{
            padding: '12px 16px', borderBottom: '1px solid #2a2a2a',
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            background: 'linear-gradient(135deg, #ff6b3520, #f7931e20)'
          }}>
            <span style={{ color: '#ff6b35', fontWeight: '600', fontSize: '14px' }}>AI Assistant</span>
            <button onClick={dismissVoice} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px' }}>
              <X size={18} color="#888" />
            </button>
          </div>

          {/* Content */}
          <div style={{ padding: '16px', minHeight: '120px' }}>
            {/* Listening state */}
            {voiceListening && (
              <div style={{ textAlign: 'center' }}>
                <div style={{
                  width: '64px', height: '64px', borderRadius: '50%', background: '#e74c3c',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  margin: '0 auto 12px', animation: 'pulse 1s ease-in-out infinite'
                }}>
                  <Mic size={28} color="#fff" />
                </div>
                <p style={{ color: '#e0e0e0', fontSize: '14px', margin: '0 0 8px' }}>Listening...</p>
                {voiceTranscript && (
                  <p style={{ color: '#888', fontSize: '13px', fontStyle: 'italic', margin: 0 }}>"{voiceTranscript}"</p>
                )}
                <button onClick={stopVoiceListening} style={{ marginTop: '12px', padding: '8px 20px', background: '#2a2a2a', border: 'none', borderRadius: '8px', color: '#e0e0e0', fontSize: '13px', cursor: 'pointer' }}>
                  Done speaking
                </button>
              </div>
            )}

            {/* Processing */}
            {voiceProcessing && (
              <div style={{ textAlign: 'center', padding: '20px 0' }}>
                <Loader2 size={28} color="#ff6b35" style={{ animation: 'spin 1s linear infinite' }} />
                <p style={{ color: '#888', fontSize: '14px', marginTop: '12px' }}>Thinking...</p>
              </div>
            )}

            {/* Reply */}
            {!voiceListening && !voiceProcessing && voiceReply && (
              <div>
                {voiceTranscript && (
                  <p style={{ color: '#666', fontSize: '12px', margin: '0 0 8px' }}>You: "{voiceTranscript}"</p>
                )}
                <div style={{
                  background: '#242424', borderRadius: '10px', padding: '12px',
                  borderLeft: '3px solid #ff6b35', marginBottom: '12px'
                }}>
                  <p style={{ color: '#e0e0e0', fontSize: '14px', margin: 0, lineHeight: '1.5' }}>{voiceReply}</p>
                </div>

                {/* Confirm actions */}
                {voiceConfirm && voiceActions.length > 0 && (
                  <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
                    <button onClick={() => executeVoiceActions(voiceActions)}
                      style={{ flex: 1, padding: '10px', background: '#27ae60', border: 'none', borderRadius: '8px', color: '#fff', fontWeight: '600', fontSize: '13px', cursor: 'pointer' }}>
                      Confirm
                    </button>
                    <button onClick={() => { setVoiceActions([]); setVoiceConfirm(false); setVoiceReply('Cancelled.'); }}
                      style={{ flex: 1, padding: '10px', background: '#2a2a2a', border: 'none', borderRadius: '8px', color: '#e0e0e0', fontWeight: '600', fontSize: '13px', cursor: 'pointer' }}>
                      Cancel
                    </button>
                  </div>
                )}

                {/* Continue conversation */}
                <button onClick={startVoiceListening}
                  style={{
                    width: '100%', padding: '10px', background: '#ff6b3520', border: '1px solid #ff6b3540',
                    borderRadius: '8px', color: '#ff6b35', fontWeight: '600', fontSize: '13px', cursor: 'pointer',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px'
                  }}>
                  <Mic size={16} /> Tap to speak again
                </button>
              </div>
            )}

            {/* Initial state (no reply yet) */}
            {!voiceListening && !voiceProcessing && !voiceReply && (
              <div style={{ textAlign: 'center', padding: '12px 0' }}>
                <p style={{ color: '#888', fontSize: '13px', margin: '0 0 12px' }}>
                  Ask me anything — pipeline summary, advance applicants, draft emails, who needs attention...
                </p>
                <button onClick={startVoiceListening}
                  style={{
                    padding: '12px 24px', background: 'linear-gradient(135deg, #ff6b35, #f7931e)',
                    border: 'none', borderRadius: '10px', color: '#fff', fontWeight: '600',
                    fontSize: '14px', cursor: 'pointer', display: 'flex', alignItems: 'center',
                    justifyContent: 'center', gap: '8px', margin: '0 auto'
                  }}>
                  <Mic size={18} /> Start talking
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {showNewApplicant && (
        <NewApplicantModal
          onClose={() => setShowNewApplicant(false)}
          onCreated={() => { setShowNewApplicant(false); loadApplicants(); }}
        />
      )}
    </div>
  );
}

function NewApplicantModal({ onClose, onCreated }) {
  const [form, setForm] = useState({
    name: '', email: '', phone: '',
    role_interest: 'Director',
    source: 'Indeed',
    city: '', state: '',
    experience_summary: '',
    sms_consent: false,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { setError('Name is required'); return; }
    setSaving(true);
    setError(null);
    try {
      const row = {
        name: form.name.trim(),
        email: form.email.trim() || null,
        phone: form.phone.trim() || null,
        role_interest: form.role_interest || null,
        source: form.source || null,
        city: form.city.trim() || null,
        state: form.state.trim() || null,
        experience_summary: form.experience_summary.trim() || null,
        sms_consent: !!form.sms_consent,
        sms_consent_at: form.sms_consent ? new Date().toISOString() : null,
        stage: 'new',
      };
      const { error: insErr } = await supabase.from('recruitment_applicants').insert([row]);
      if (insErr) throw insErr;
      onCreated();
    } catch (err) {
      setError(err.message || 'Failed to add applicant');
    } finally {
      setSaving(false);
    }
  };

  const inputStyle = {
    width: '100%', padding: '0.6rem 0.75rem', background: '#0a0a0a',
    border: '1px solid #2a2a2a', borderRadius: '6px', color: '#e0e0e0',
    fontSize: '0.9rem', boxSizing: 'border-box',
  };
  const labelStyle = { display: 'block', color: '#aaa', fontSize: '0.8rem', marginBottom: '0.3rem' };

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)',
        display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
        padding: '2rem 1rem', zIndex: 1000, overflowY: 'auto',
      }}
    >
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={submit}
        style={{
          background: '#1a1a1a', borderRadius: '12px', padding: '1.5rem',
          width: '100%', maxWidth: '480px', border: '1px solid #3a3a3a',
          marginBottom: '2rem', boxSizing: 'border-box',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem' }}>
          <h2 style={{ color: '#e0e0e0', fontWeight: 700, margin: 0, fontSize: '1.15rem' }}>New applicant</h2>
          <button type="button" onClick={onClose} style={{ background: 'none', border: 'none', color: '#888', cursor: 'pointer', fontSize: '1.25rem' }}>×</button>
        </div>

        {error && (
          <div style={{ padding: '0.6rem 0.75rem', background: '#3a1f1f', border: '1px solid #e74c3c44', borderRadius: '6px', color: '#e74c3c', fontSize: '0.85rem', marginBottom: '0.75rem' }}>
            {error}
          </div>
        )}

        <div style={{ marginBottom: '0.75rem' }}>
          <label style={labelStyle}>Name *</label>
          <input style={inputStyle} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.6rem', marginBottom: '0.75rem' }}>
          <div>
            <label style={labelStyle}>Email</label>
            <input type="email" style={inputStyle} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
          </div>
          <div>
            <label style={labelStyle}>Phone</label>
            <input type="tel" style={inputStyle} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.6rem', marginBottom: '0.75rem' }}>
          <div>
            <label style={labelStyle}>Role interest</label>
            <select style={inputStyle} value={form.role_interest} onChange={(e) => setForm({ ...form, role_interest: e.target.value })}>
              <option>Director</option>
              <option>Manager</option>
              <option>Affiliate</option>
              <option>Other</option>
            </select>
          </div>
          <div>
            <label style={labelStyle}>Source</label>
            <select style={inputStyle} value={form.source} onChange={(e) => setForm({ ...form, source: e.target.value })}>
              <option>Indeed</option>
              <option>LinkedIn</option>
              <option>Referral</option>
              <option>Direct</option>
              <option>Other</option>
            </select>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '0.6rem', marginBottom: '0.75rem' }}>
          <div>
            <label style={labelStyle}>City</label>
            <input style={inputStyle} value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} />
          </div>
          <div>
            <label style={labelStyle}>State</label>
            <input style={inputStyle} maxLength={2} value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value.toUpperCase() })} />
          </div>
        </div>

        <div style={{ marginBottom: '0.75rem' }}>
          <label style={labelStyle}>Notes / experience summary</label>
          <textarea
            style={{ ...inputStyle, minHeight: '70px', resize: 'vertical' }}
            value={form.experience_summary}
            onChange={(e) => setForm({ ...form, experience_summary: e.target.value })}
          />
        </div>

        <label style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem', color: '#aaa', fontSize: '0.8rem', marginBottom: '1rem', cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={form.sms_consent}
            onChange={(e) => setForm({ ...form, sms_consent: e.target.checked })}
            style={{ marginTop: '2px' }}
          />
          <span>SMS consent — they agreed to receive text messages from us</span>
        </label>

        <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
          <button type="button" onClick={onClose} disabled={saving} style={{ padding: '0.55rem 1rem', background: '#2a2a2a', border: '1px solid #3a3a3a', borderRadius: '6px', color: '#aaa', cursor: 'pointer' }}>
            Cancel
          </button>
          <button type="submit" disabled={saving || !form.name.trim()} style={{ padding: '0.55rem 1.1rem', background: '#ff6b35', border: 'none', borderRadius: '6px', color: '#fff', fontWeight: 600, cursor: saving ? 'wait' : 'pointer', opacity: (!form.name.trim() || saving) ? 0.6 : 1 }}>
            {saving ? 'Adding…' : 'Add applicant'}
          </button>
        </div>
      </form>
    </div>
  );
}
